import { useEffect, useRef, useState } from 'react';

const pills = [
  { icon: 'ri-checkbox-circle-line', label: 'Stav projektu v reálnom čase' },
  { icon: 'ri-task-line', label: 'Úlohy a termíny' },
  { icon: 'ri-bar-chart-2-line', label: 'Mesačné reporty' },
  { icon: 'ri-wifi-line', label: 'Online odkiaľkoľvek, 24/7' },
];

const comparison = [
  {
    label: 'Iné agentúry',
    icon: 'ri-close-circle-line',
    color: 'text-red-400',
    bg: 'bg-red-500/5 border-red-500/10',
    items: [
      'Napíšete email: „Ako to ide?"',
      'Čakáte 2–3 dni na odpoveď',
      'Dostanete vyhýbavé „pracujeme na tom"',
      'Nevedno čo presne, kedy a za koľko',
    ],
  },
  {
    label: 'Boostio',
    icon: 'ri-checkbox-circle-line',
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/5 border-emerald-500/15',
    items: [
      'Prihlásíte sa do portálu - hoci o polnoci',
      'Vidíte fázy projektu, percento dokončenia',
      'Otvorené úlohy, termíny, aktívne služby',
      'Reporty, faktúry - všetko na klik',
    ],
  },
];

export default function KPHero() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center bg-zinc-950 overflow-hidden pt-24 pb-20">
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(249,115,22,0.12) 0%, transparent 65%)' }}
      />
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.025]"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />

      <div ref={ref} className="relative z-10 max-w-5xl mx-auto px-6 w-full">
        <div className="text-center">
          {/* Badge */}
          <div className={`inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-1.5 mb-7 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="w-2 h-2 bg-orange-400 rounded-full animate-pulse" />
            <span className="text-orange-400 text-sm font-semibold">Klientský portál - exkluzívne pre klientov Boostio</span>
          </div>

          {/* Heading */}
          <h1 className={`text-5xl md:text-7xl font-black text-white leading-[1.05] mb-5 tracking-tight transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            Koniec pýtaniu sa,{' '}
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-orange-500 to-amber-400">
              čo sa deje s projektom.
            </span>
          </h1>

          {/* Subtitle */}
          <p className={`text-zinc-400 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto mb-10 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            Každý klient Boostio má vlastný online portál. Vidíte stav projektu, úlohy, faktúry
            aj reporty - <strong className="text-white">sami, okamžite, odkiaľkoľvek.</strong>
            {' '}Bez čakania na správu od nás.
          </p>

          {/* Pills */}
          <div className={`flex flex-wrap items-center justify-center gap-3 mb-14 transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            {pills.map((p) => (
              <div key={p.label} className="inline-flex items-center gap-2 bg-zinc-900/80 border border-zinc-700/60 rounded-full px-4 py-2">
                <i className={`${p.icon} text-orange-400 text-sm`} />
                <span className="text-zinc-300 text-sm font-medium">{p.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Comparison block */}
        <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 mb-12 transition-all duration-700 delay-400 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          {comparison.map((side) => (
            <div key={side.label} className={`rounded-2xl border p-6 ${side.bg}`}>
              <div className="flex items-center gap-2 mb-4">
                <i className={`${side.icon} ${side.color} text-lg`} />
                <span className={`font-bold text-sm ${side.color}`}>{side.label}</span>
              </div>
              <ul className="space-y-2.5">
                {side.items.map((item) => (
                  <li key={item} className="flex items-start gap-2.5">
                    <i className={`${side.icon} ${side.color} text-sm flex-shrink-0 mt-0.5`} />
                    <span className="text-zinc-300 text-sm leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className={`flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <a
            href="/#kontakt"
            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-bold px-7 py-4 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap text-base"
          >
            Chcem sa stať klientom
            <i className="ri-arrow-right-line" />
          </a>
          <a
            href="#ukazka"
            onClick={(e) => { e.preventDefault(); document.querySelector('#ukazka')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-white font-semibold px-7 py-4 rounded-xl border border-zinc-800 hover:border-zinc-600 transition-all duration-200 cursor-pointer whitespace-nowrap text-base"
          >
            <i className="ri-play-circle-line" />
            Pozrieť ukážku portálu
          </a>
        </div>
      </div>
    </section>
  );
}
